<template>
  <div class="flex flex-col gap-4 h-full min-h-0">
    <div class="flex flex-row gap-2 justify-end text-[var(--ld-brand-500)]">
      <span
        class="cursor-pointer hover:opacity-80"
        @click="selectAll"
      >
        {{ _t('cardview.ai_gen_select_all') }}
      </span>
      <span
        class="cursor-pointer hover:opacity-80"
        @click="deselectAll"
      >
        {{ _t('cardview.ai_gen_deselect_all') }}
      </span>
    </div>
    <div
      ref="gridContainer"
      class="pdf-thumbnail-grid overflow-y-auto flex-1 min-h-0"
    >
      <div
        v-for="pageNum in totalPages"
        :key="pageNum"
        class="thumbnail-item"
        :class="{ selected: pagesSelectingStatus[pageNum] }"
        @click="select(pageNum)"
      >
        <div
          :ref="el => observeElement(el as HTMLElement, pageNum)"
          class="canvas-container"
        >
          <canvas
            v-show="canvasRefs[pageNum - 1]"
            :ref="
              el => {
                canvasRefs[pageNum - 1] = el as HTMLCanvasElement
              }
            "
          ></canvas>
          <div
            v-show="!pageRenderStatus[pageNum - 1]"
            class="loading-placeholder"
          >
            <i
              class="pi pi-spin pi-spinner text-[var(--ld-label-secondary)] text-24px"
            ></i>
          </div>
          <Icon
            v-show="pagesSelectingStatus[pageNum]"
            name="checked"
            class="absolute w-16px bottom-8px right-8px"
          />
        </div>
        <div class="page-number">{{ pageNum }}</div>
      </div>
    </div>
    <div class="flex flex-row justify-end">
      <Button @click="done">{{ _t('common.continue') }}</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, onMounted } from 'vue'
import * as pdfjsLib from 'pdfjs-dist'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import Button from '@/components/Button.vue'
import Icon from '@/components/Icon.vue'
import { usePdfPageCacheStore } from '@/stores/pdfPageCache'

// 定义 props
const props = defineProps<{
  pdfUrl: string // PDF文件的URL
}>()

// 双向绑定选中的页面数组
const selectedPages = defineModel<number[]>('selectedPages', {
  required: true,
})

// 定义事件
const emit = defineEmits<{
  done: [] // 完成选择时触发
}>()

// 初始化 PDF.js worker，用于在后台线程处理PDF文件
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc

// 状态管理
const gridContainer = ref<HTMLElement | null>(null) // 缩略图网格容器引用
const canvasRefs = ref<HTMLCanvasElement[]>([]) // 存储每个页面的 canvas 元素
const totalPages = ref(0) // PDF 总页数
const error = ref('') // 错误信息
const pagesSelectingStatus = ref<boolean[]>([]) // 每个页面的选中状态
const pageRenderStatus = ref<boolean[]>([]) // 每个页面的渲染状态
const pdfPageCacheStore = usePdfPageCacheStore() // PDF 页面缓存 store

// 控制变量
let isUnmounted = false // 组件是否已卸载的标志
let abortController = new AbortController() // 用于取消加载任务的控制器

// 添加 Intersection Observer 相关变量
const observer = ref<IntersectionObserver | null>(null)
const observedElements = new Map<number, HTMLElement>()

// 页面渲染队列管理
const MAX_CONCURRENT_RENDERS = 10 // 最大并发渲染数量
const renderQueue = ref<number[]>([]) // 等待渲染的页面队列
const renderingPages = ref(new Set<number>()) // 正在渲染的页面集合
let pdfDoc: pdfjsLib.PDFDocumentProxy | null = null // 缓存的 PDF 文档实例

// 处理渲染队列
const processRenderQueue = async () => {
  if (isUnmounted) return

  // 如果当前渲染的页面数量已达到上限，不处理新的渲染请求
  if (renderingPages.value.size >= MAX_CONCURRENT_RENDERS) {
    return
  }

  // 当队列中有页面且未达到最大并发数时，继续处理
  while (
    renderQueue.value.length > 0 &&
    renderingPages.value.size < MAX_CONCURRENT_RENDERS
  ) {
    const pageNum = renderQueue.value.shift()
    // 跳过已渲染或正在渲染的页面
    if (
      !pageNum ||
      renderingPages.value.has(pageNum) ||
      pageRenderStatus.value[pageNum - 1]
    ) {
      continue
    }

    // 确保不超过最大并发数
    if (renderingPages.value.size >= MAX_CONCURRENT_RENDERS) {
      renderQueue.value.unshift(pageNum) // 将页面放回队列开头
      break
    }

    renderPage(pageNum, pdfDoc)
  }
}

// Intersection Observer 回调函数
const initializeObserver = () => {
  observer.value = new IntersectionObserver(
    entries => {
      const visiblePages = new Set<number>() // 记录当前可见的页面

      entries.forEach(entry => {
        const element = entry.target as HTMLElement
        const pageNum = parseInt(element.dataset.pageNum || '0')
        if (!pageNum) return

        if (entry.isIntersecting) {
          visiblePages.add(pageNum)

          // 如果页面已经渲染完成，跳过
          if (pageRenderStatus.value[pageNum - 1]) {
            return
          }

          // 从队列中移除该页面（如果存在）
          const index = renderQueue.value.indexOf(pageNum)
          if (index !== -1) {
            renderQueue.value.splice(index, 1)
          }

          // 将新页面添加到队列开头（优先渲染）
          if (!renderingPages.value.has(pageNum)) {
            renderQueue.value.unshift(pageNum)
          }
        } else {
          // 如果页面离开视口且在队列中且不在渲染中，将其移到队列末尾（降低优先级）
          if (!renderingPages.value.has(pageNum)) {
            const index = renderQueue.value.indexOf(pageNum)
            if (index !== -1) {
              renderQueue.value.splice(index, 1)
              renderQueue.value.push(pageNum)
            }
          }
        }
      })

      // 处理渲染队列
      processRenderQueue()
    },
    {
      root: gridContainer.value,
      rootMargin: '100px 0px', // 预加载视口外 100px 的内容
      threshold: 0.1, // 当页面有 10% 进入视口时触发
    }
  )
}

// 观察元素
const observeElement = (element: HTMLElement | null, pageNum: number) => {
  if (!element) return

  // 移除旧的观察
  const oldElement = observedElements.get(pageNum)
  if (oldElement && observer.value) {
    observer.value.unobserve(oldElement)
  }

  // 添加新的观察
  element.dataset.pageNum = pageNum.toString()
  if (observer.value) {
    observer.value.observe(element)
    observedElements.set(pageNum, element)
  }
}

// 组件挂载时初始化 observer
onMounted(() => {
  initializeObserver()
})

// 清理函数：用于组件卸载或重新加载时清理状态
const cleanup = () => {
  isUnmounted = true
  abortController.abort() // 取消所有正在进行的加载任务
  renderQueue.value = [] // 清空渲染队列
  renderingPages.value.clear() // 清理渲染状态
  pdfDoc = null // 清理 PDF 文档实例

  // 清理 observer
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
  observedElements.clear()
}

// 组件卸载时的清理
onUnmounted(() => {
  cleanup()
})

// 渲染单个页面
const renderPage = async (
  pageNum: number,
  pdf: pdfjsLib.PDFDocumentProxy | null
) => {
  // 跳过已渲染或正在渲染的页面
  if (
    isUnmounted ||
    renderingPages.value.has(pageNum) ||
    pageRenderStatus.value[pageNum - 1]
  ) {
    return
  }

  renderingPages.value.add(pageNum)
  pageRenderStatus.value[pageNum - 1] = false

  try {
    // 检查缓存中是否有缩略图
    const cachedThumbnail = pdfPageCacheStore.getThumbnail(
      props.pdfUrl as string,
      pageNum
    )
    const canvas = canvasRefs.value[pageNum - 1]

    if (!canvas || isUnmounted) {
      renderingPages.value.delete(pageNum)
      processRenderQueue()
      return
    }

    // 如果有缓存的缩略图，直接使用
    if (cachedThumbnail) {
      const img = new Image()
      img.onload = () => {
        if (isUnmounted) return
        const context = canvas.getContext('2d')
        if (!context) return
        canvas.height = img.height
        canvas.width = img.width
        context.drawImage(img, 0, 0)
        pageRenderStatus.value[pageNum - 1] = true
        renderingPages.value.delete(pageNum)
        processRenderQueue()
      }
      img.onerror = () => {
        renderingPages.value.delete(pageNum)
        processRenderQueue()
      }
      img.src = cachedThumbnail
      return
    }

    // 如果没有缓存，从 PDF 文档渲染
    if (!pdf && !pdfDoc) {
      const pdfSource = {
        url: props.pdfUrl,
        signal: abortController.signal,
      }
      pdfDoc = await pdfjsLib.getDocument(pdfSource).promise
      pdf = pdfDoc
    }

    if (isUnmounted || !pdf) {
      renderingPages.value.delete(pageNum)
      processRenderQueue()
      return
    }

    // 获取页面并设置渲染参数
    const page = await pdf.getPage(pageNum)
    const viewport = page.getViewport({ scale: 0.3 }) // 缩放比例 0.3 用于生成缩略图
    const context = canvas.getContext('2d')

    if (!context || isUnmounted) {
      renderingPages.value.delete(pageNum)
      processRenderQueue()
      return
    }

    canvas.height = viewport.height
    canvas.width = viewport.width

    try {
      // 渲染页面到 canvas
      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise

      if (!isUnmounted) {
        // 将渲染结果保存到缓存
        const thumbnail = canvas.toDataURL('image/jpeg', 0.5)
        pdfPageCacheStore.addThumbnail(
          props.pdfUrl as string,
          pageNum,
          totalPages.value,
          thumbnail
        )
        pageRenderStatus.value[pageNum - 1] = true
      }
    } finally {
      renderingPages.value.delete(pageNum)
      processRenderQueue()
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') return // 忽略取消操作导致的错误
    console.error(`Error rendering page ${pageNum}:`, err)
    pageRenderStatus.value[pageNum - 1] = false
    renderingPages.value.delete(pageNum)
    processRenderQueue()
  }
}

// 渲染所有PDF页面
const renderPdf = async () => {
  error.value = ''

  // 重置所有状态
  cleanup()
  isUnmounted = false
  abortController = new AbortController()

  try {
    // 尝试从缓存获取总页数
    const cachedTotalPages = pdfPageCacheStore.getTotalPages(
      props.pdfUrl as string
    )
    if (cachedTotalPages) {
      totalPages.value = cachedTotalPages
    } else {
      // 如果缓存中没有，需要加载PDF文档获取页数
      const pdfSource = {
        url: props.pdfUrl,
        signal: abortController.signal,
      }
      const pdf = await pdfjsLib.getDocument(pdfSource).promise
      totalPages.value = pdf.numPages
    }

    // 初始化数组
    canvasRefs.value = new Array(totalPages.value)
    pageRenderStatus.value = new Array(totalPages.value).fill(false)
    pagesSelectingStatus.value = new Array(totalPages.value + 1).fill(false)

    // 恢复之前选中的页面状态
    for (const page of selectedPages.value) {
      pagesSelectingStatus.value[page] = true
    }

    // 等待DOM更新，确保canvas元素已创建
    await new Promise(resolve => setTimeout(resolve, 0))

    // 重新初始化 observer
    initializeObserver()
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'AbortError') return
    error.value = '无法加载PDF文件'
    console.error('Error loading PDF:', err)
  }
}

// 页面选择逻辑
const select = (pageNum: number) => {
  pagesSelectingStatus.value[pageNum] = !pagesSelectingStatus.value[pageNum]
}

// 全选
const selectAll = () => {
  for (let i = 1; i <= totalPages.value; ++i) {
    pagesSelectingStatus.value[i] = true
  }
}

// 取消全选
const deselectAll = () => {
  for (let i = 1; i <= totalPages.value; ++i) {
    pagesSelectingStatus.value[i] = false
  }
}

// 完成选择
const done = () => {
  let pages = []
  for (let i = 1; i <= totalPages.value; ++i) {
    if (pagesSelectingStatus.value[i]) {
      pages.push(i)
    }
  }
  selectedPages.value = pages
  emit('done')
}

// 监听 PDF URL 变化
watch(
  () => props.pdfUrl,
  () => {
    if (props.pdfUrl) {
      pdfDoc = null // 重置 PDF 文档实例
      renderPdf()
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.pdf-thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  padding: 8px;
  scrollbar-width: thin;
  scrollbar-color: var(--ld-brand-500) transparent;
}

.thumbnail-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.canvas-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
}

.canvas-container canvas {
  width: 100%;
  display: block;
  vertical-align: bottom;
}

.loading-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  z-index: 1;
}

.selected .canvas-container {
  outline: 2px solid var(--ld-brand-500);
  outline-offset: 2px;
  border-radius: 8px;
}

.page-number {
  font-size: 14px;
  color: var(--ld-text-400);
  line-height: 1;
  margin-top: 8px;
}

/* 滚动条样式 */
.pdf-thumbnail-grid::-webkit-scrollbar {
  width: 6px;
}

.pdf-thumbnail-grid::-webkit-scrollbar-track {
  background: transparent;
}

.pdf-thumbnail-grid::-webkit-scrollbar-thumb {
  background: var(--ld-brand-500);
  border-radius: 3px;
}
</style>
